import { useState } from 'react';
import { useAction, useMutation, useQuery } from 'convex/react';
import { useParams } from 'react-router-dom';
import { api } from '../../../../convex/_generated/api';
import type { Id } from '../../../../convex/_generated/dataModel';

/**
 * Client detail, and the portal links that are the whole retention strategy.
 *
 * The plaintext token is shown exactly once, at creation. After that only the label, the
 * view count and the revoke button remain — losing a link means minting a new one, which is
 * the correct trade for not storing a live credential.
 */
export default function ClientDetail() {
  const { clientId } = useParams<{ clientId: string }>();
  const id = clientId as Id<'clients'>;
  const client = useQuery(api.clients.get, { clientId: id });
  const sites = useQuery(api.sites.listByClient, { clientId: id });
  const links = useQuery(api.portal.listLinks, { clientId: id });
  const createLink = useAction(api.portal.createLink);
  const revoke = useMutation(api.portal.revokeLink);

  const [label, setLabel] = useState('');
  const [fresh, setFresh] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function mint() {
    setBusy(true);
    try {
      const result = await createLink({ clientId: id, ...(label.trim() ? { label: label.trim() } : {}) });
      setFresh(result.url);
      setLabel('');
    } finally {
      setBusy(false);
    }
  }

  if (client === undefined) return <p className="muted">Loading…</p>;

  return (
    <>
      <h1>{client.name}</h1>
      <p className="muted">{client.billingEmail ?? client.billingPhone ?? 'No contact details'}</p>

      <h2>Compliance portal</h2>
      <div className="card">
        <p className="muted small">
          A read-only register branded to you. Send one link per person — each can be revoked
          on its own, and you can see when it was last opened.
        </p>
        <label>Who is this link for?</label>
        <div className="row">
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Jane, WHS officer" />
          <button disabled={busy} onClick={() => void mint()}>Create link</button>
        </div>

        {fresh ? (
          <>
            <p className="small"><strong>Copy this now — it is not shown again.</strong></p>
            <code className="token">{fresh}</code>
            <button className="quiet" onClick={() => void navigator.clipboard.writeText(fresh)}>
              Copy link
            </button>
          </>
        ) : null}

        <table style={{ marginTop: '1rem' }}>
          <thead><tr><th>Label</th><th>Created</th><th>Last opened</th><th>Views</th><th /></tr></thead>
          <tbody>
            {(links ?? []).map((l) => (
              <tr key={l._id}>
                <td>{l.label ?? '—'}{l.revokedAt ? <span className="pill">revoked</span> : null}</td>
                <td className="muted">{new Date(l.createdAt).toLocaleDateString('en-AU')}</td>
                <td className="muted">
                  {l.lastViewedAt ? new Date(l.lastViewedAt).toLocaleDateString('en-AU') : 'never'}
                </td>
                <td>{l.viewCount}</td>
                <td>
                  {l.revokedAt ? null : (
                    <button className="danger" onClick={() => void revoke({ linkId: l._id })}>Revoke</button>
                  )}
                </td>
              </tr>
            ))}
            {links?.length === 0 ? (
              <tr><td colSpan={5} className="muted">No portal links yet.</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <h2>Sites</h2>
      <div className="card">
        <table>
          <thead><tr><th>Site</th><th>Address</th><th>Environment</th></tr></thead>
          <tbody>
            {(sites ?? []).map((s) => (
              <tr key={s._id}>
                <td>{s.name}</td>
                <td className="muted">{s.address}</td>
                <td className="muted small">{s.defaultEnvironmentClass.replace(/_/g, ' ')}</td>
              </tr>
            ))}
            {sites?.length === 0 ? (
              <tr><td colSpan={3} className="muted">No sites yet — add them from the field app.</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </>
  );
}
